import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

//components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import TimePickerInput from '../../../components/TimePicker';
import InputTime from '../../../components/GenericForm/Widgets/InputTime';
import UserPromptModal from '../../../components/GenericModal/userPromptModal/index.js';

// Elements
import Button from '../../../components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { shortDateFormat } from '~/utils/dateFormat';
import { minutesToTimeInput, getMinutesFromTimeInput } from '~/utils/timeCalc';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import { TIMESHEET_CONSTANT } from '../../../utils/constants.js';

class EditTimesheetTimeModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isSavingTime: false,
            editTimelogData: null,
            originalData: null,
            userPromptModal: {
                source: '',
                header: '',
                showModal: false,
                sourceInfo: {},
                actionMethods: {}
            }
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && this.props.editTimelogId && (prevProps.editTimelogId !== this.props.editTimelogId || !prevProps.show)) {
            this.loadTimelog();
        }
    }

    loadTimelog = async () => {
        this.setState({ isSavingTime: true });
        try {
            this.setState({ editTimelogData: this.props.editTimelogData, originalData: this.props.editTimelogData, });
        } catch (error) {
            console.error("Failed to load timelog", error);
        } finally {
            this.setState({ isSavingTime: false });
        }
    }

    handleTimeChange = (name, value) => {
        // this.props.timelogStore.changeEntity(name, value);
        const minutes = (name === 'from' || name === 'to') ? getMinutesFromTimeInput(value) : value;
        this.setState({ editTimelogData: { ...this.state.editTimelogData, [name]: minutes } });
    }


    handleCloseUserPromptModal = () => {
        this.setState({ userPromptModal: { ...this.state.userPromptModal, showModal: false } })
    }

    handleResetApprovedWeeklyOvertime = async (approvedTimesheets) => {
        const { timelogStore, commonStore, t, onHide, onSaved } = this.props;
        this.setState({ isSavingTime: true });
        try {
            await timelogStore.resetApprovedWeeklyOvertimeTimesheets(approvedTimesheets);
            this.handleCloseUserPromptModal();
            commonStore.addNotification(t('Approved timesheets have been reset successfully.'), null, 'success');
            onHide();
            onSaved?.(this.state.editTimelogData);
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error resetting timesheet'), null, 'error');
        } finally {
            this.setState({ isSavingTime: false });
        }
    }

    handleSave = async () => {
        const { timelogStore, commonStore, t, onHide, onSaved } = this.props;
        const { editTimelogData, originalData } = this.state;

        const fromMinutes = (editTimelogData.from);
        const endMinutes = (editTimelogData.to);
        let breakMinutes = editTimelogData.break || 0; // Math.round(data.break / 15) * 15;
        let totalMinutes = endMinutes - fromMinutes;
        if (endMinutes < fromMinutes) {
            // && fromMinutes > (12 * 60)
            // this is probably 12-h shift
            totalMinutes += 24 * 60;
        }
        // No breaks if less than 5.5 hours
        if (editTimelogData.status === 'draft' && totalMinutes <= TIMESHEET_CONSTANT.MINIMUM_HOURS_FOR_BREAK_IN_MIN) {
            breakMinutes = 0;
        }
        editTimelogData.break = breakMinutes;
        editTimelogData.breakMinutes = breakMinutes;
        if (totalMinutes > breakMinutes) {
            totalMinutes -= breakMinutes;
        } else {
        }
        const totalResultPure = minutesToTimeInput(totalMinutes, 'full');
        editTimelogData.total_hours_overall = totalResultPure.positive ? totalResultPure.value : '00:00';
        totalMinutes -= getMinutesFromTimeInput(editTimelogData.total_hours_overtime);
        const totalResult = minutesToTimeInput(totalMinutes, 'full');
        editTimelogData.total_hours_normal = totalResult.positive ? totalResult.value : '00:00';

        const clientData = commonStore.config?.client?.data;

        if (editTimelogData?.gps_data?.systemEntry &&
            clientData?.loginRules?.allowUserToEditAutoTimesheet) {
            editTimelogData.changeSystemEntryToManual = true;
        }

        const isAllowManualSwitch = clientData?.basicRules && !clientData?.basicRules?.allowManualTimelogChange && editTimelogData.tracker_status === 'done' && !editTimelogData.is_manually_managed;
        const switchToManual = isAllowManualSwitch && (editTimelogData.from !== originalData.from || editTimelogData.to !== originalData.to)
        if (switchToManual) {
            if (editTimelogData.gps_data) {
                editTimelogData.gps_data.start = { manual: true };
                editTimelogData.gps_data.end = { manual: true };
            }
            editTimelogData.tracker_status = 'manual';
        }

        this.setState({ isSavingTime: true });
        try {
            const res = await timelogStore.quickTimeUpdate(editTimelogData);
            const intersection = res?.userPrompts?.find(ele => ele.source === 'intersection')
            if (intersection?.showModal) {
                this.setState({ userPromptModal: { ...intersection, actionMethods: { handleCloseUserPromptModal: this.handleCloseUserPromptModal } } });
            }
            const approvedWeeklyOvertime = res?.userPrompts?.find(ele => ele.source === 'approvedWeeklyOvertime')
            if (approvedWeeklyOvertime?.showModal) {
                this.setState({
                    userPromptModal: {
                        ...approvedWeeklyOvertime, actionMethods: {
                            handleCloseUserPromptModal: this.handleCloseUserPromptModal,
                            handleResetApprovedWeeklyOvertime: () => this.handleResetApprovedWeeklyOvertime(approvedWeeklyOvertime.sourceInfo.approvedWeeklyOvertime.approvedTimesheets)
                        }
                    }
                });
            }
            if (res && (res.timelog || res.id)) {
                commonStore.addNotification(t('Timesheet updated successfully'), null, 'success');
                onSaved?.(res.timelog || res);
                // Already saved — the comment is only a suggestion, so keep this modal open
                // for it and close once the user has either added it or dismissed it.
                const overtimeComment = res?.userPrompts?.find(ele => ele.source === 'overtimeComment');
                if (overtimeComment?.showModal) {
                    return this.setState({
                        userPromptModal: {
                            ...overtimeComment,
                            actionMethods: {
                                handleSaveOvertimeComment: async (comment) => {
                                    try {
                                        await timelogStore.saveOvertimeComment((res.timelog || res).id, comment);
                                    } catch (err) {
                                        // Stay on the prompt so the comment can be corrected
                                        console.error({ err });
                                        return;
                                    }
                                    this.handleCloseUserPromptModal();
                                    onHide();
                                },
                                handleCloseUserPromptModal: () => {
                                    this.handleCloseUserPromptModal();
                                    onHide();
                                },
                            }
                        }
                    });
                }
                onHide();
            }
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error updating timesheet'), null, 'error');
        } finally {
            this.setState({ isSavingTime: false });
        }
    }

    render() {
        const { show, onHide, t, timelogStore, commonStore, message } = this.props;
        const { isSavingTime, userPromptModal, editTimelogData, originalData } = this.state;

        const clientData = commonStore.config?.client?.data;
        const dateTimeRules = commonStore.config?.client?.data?.dateTimeRules || false;
        const format = dateTimeRules?.time_format || 'hh:mm';
        const hideBreakButton = !!(commonStore.config?.client?.data?.basicRules?.hideBreakButton);

        if (!show || !editTimelogData) return;

        let disableEdit = false;
        let disableEditMessage = '';

        const isSystemEntry = editTimelogData?.gps_data?.systemEntry;
        const allowUserToEditAutoTimesheet = clientData?.loginRules?.allowUserToEditAutoTimesheet;

        if (editTimelogData?.data?.timeBank?.length && editTimelogData?.data?.timeBank?.some(ele => ele.type === 'overtime')) {
            disableEdit = true;
            disableEditMessage = t('Cannot edit this timesheet as it contains timebank hours.');
        } else if (editTimelogData?.is_manually_managed) {
            disableEdit = true;
            disableEditMessage = t('This timesheet have Mandatory Overtime/Flexitime, please view full timesheet and edit accordingly.');
        } else if (editTimelogData?.isManualOvertime) {
            disableEdit = true;
            disableEditMessage = t('This timesheet have manual overtime, please view full timesheet and edit accordingly.');
        } else if (isSystemEntry && !allowUserToEditAutoTimesheet) {
            disableEdit = true;
            disableEditMessage = t('Cannot edit time as its system entry');
        }
        const isAllowManualSwitch = clientData?.basicRules && !clientData?.basicRules?.allowManualTimelogChange && editTimelogData.tracker_status === 'done' && !editTimelogData.is_manually_managed;

        return (
            <>
                <Modal
                    show={show}
                    onHide={onHide}
                    centered
                    size="md"
                    className="stacked-modal-1060-zindex"
                    backdropClassName="stacked-modal-backdrop-1060-zindex"
                >
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0 fs-5">{t('Edit Timesheet Time')}</Modal.Title>
                        <img src={CloseModalIcon} className="cursor-pointer" alt="close icon" onClick={onHide} />
                    </Modal.Header>

                    <Modal.Body className='py-0'>
                        <div style={{ marginRight: '20px' }}>
                            {this.state.isSavingTime ? (
                                <div className="text-center"><LoadingSpinner /></div>
                            ) : (
                                <div>
                                    {message && !disableEditMessage && <div className='fw-bold custom-customer-header mt-2 fs15'>{message}</div>}
                                    {disableEditMessage && <div className='fw-bold custom-customer-header mt-2 fs15'>{disableEditMessage}</div>}
                                    {isAllowManualSwitch && <div className='fw-bold custom-customer-header mt-2 fs15'>{t('This will switch timesheet to manual.')}</div>}
                                    <div className='pt-2'>
                                        <Row>
                                            <Col xs={12} md={3} className="mb-3">
                                                <label className="form-label">{t('Date')}</label>
                                                <div className='mt-1 fw-bold'>{shortDateFormat(editTimelogData.for_date, dateTimeRules)}</div>
                                            </Col>
                                            <Col xs={12} md={3} className="mb-3">
                                                <label className="form-label">{t('Start Time')}</label>
                                                <TimePickerInput
                                                    value={minutesToTimeInput(editTimelogData.from)}
                                                    format={format === "hh:mm" ? "HH:mm" : format}
                                                    onChange={(val) => this.handleTimeChange('from', val)}
                                                    style={{ width: 150 }}
                                                    disabled={disableEdit}
                                                />
                                            </Col>
                                            <Col xs={12} md={3} className="mb-3">
                                                <label className="form-label">{t('End Time')}</label>
                                                <TimePickerInput
                                                    value={minutesToTimeInput(editTimelogData.to)}
                                                    format={format === "hh:mm" ? "HH:mm" : format}
                                                    onChange={(val) => this.handleTimeChange('to', val)}
                                                    style={{ width: 150 }}
                                                    disabled={disableEdit}
                                                />
                                            </Col>
                                            <Col xs={12} md={3} className="mb-3">
                                                <label className="form-label">{t('Break')}</label>
                                                {(
                                                    <InputTime
                                                        value={minutesToTimeInput(editTimelogData.break, false)}
                                                        onChange={(e) => {
                                                            this.handleTimeChange('break', getMinutesFromTimeInput(e))
                                                        }}
                                                        className={`big-input text-center`}
                                                        disabled={true}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            )}
                        </div>

                        <UserPromptModal
                            source={userPromptModal.source}
                            header={userPromptModal.header}
                            showModal={userPromptModal.showModal}
                            sourceInfo={userPromptModal.sourceInfo}
                            actions={userPromptModal.actions}
                            actionMethods={userPromptModal.actionMethods}
                        />
                    </Modal.Body>

                    <Modal.Footer>
                        <Button className='fw-bold' onClick={() => this.props.router.navigate(`/admin/timelogs/${this.state.editTimelogData.id}`, { state: { timelogs: this.state.editTimelogData.id, page: this.props.page || 0, lastInteractedId: timelogStore.lastInteractedId } })} disabled={isSavingTime}>{t('View Full Timesheet')}</Button>
                        <OverlayTrigger
                            key="edit"
                            placement="top"
                            overlay={
                                <Tooltip id="tooltip-top">
                                    {disableEdit ? disableEditMessage : t('Submit')}
                                </Tooltip>
                            }
                        >
                            <Button fill
                                onClick={() => {
                                    if (isSystemEntry && allowUserToEditAutoTimesheet) {
                                        confirmAlert({
                                            title: t('Edit System Entry'),
                                            message: t('You are editing system entry, this will switch timesheet to manual'),
                                            buttons: [
                                                {
                                                    label: t('Yes, proceed'),
                                                    onClick: () => {
                                                        setTimeout(() => {
                                                            this.handleSave()
                                                        }, 0)
                                                    },
                                                },
                                                {
                                                    label: t('Cancel')
                                                },
                                            ],
                                        });
                                        return;
                                    }
                                    this.handleSave();
                                }}
                                disabled={isSavingTime || disableEdit}
                            >
                                {t('Submit')}
                            </Button>
                        </OverlayTrigger>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default inject('timelogStore', 'commonStore')(applicationRouter(withLocalization(observer(EditTimesheetTimeModal))));